import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ICenterChainEndOffer } from '@nkr/models/index';
import { ReportWrapper } from './styles';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, RowDataChangedEvent, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';
import { Utility } from '@nkr/services/utility';

interface IProps {
    data: ICenterChainEndOffer[],
    onGridReady: (e: GridReadyEvent) => void,
    onFirstDataRendered: (e: FirstDataRenderedEvent) => void,
    onRowDataChanged: (e: RowDataChangedEvent) => void,
    onSortChanged: (e: SortChangedEvent) => void,
}

const Grid = (props: IProps) => {
    const columns: ColDef[] = [
        {
            colId: 'rank',
            headerName: '#',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>#</SortableHeaderComponent>,
            width: 40,
            valueGetter: (params: ValueGetterParams) => params.node.rowIndex + 1
        },

        {
            colId: 'matchHistoryId',
            field: 'matchHistoryId',
            headerName: 'Offer #',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Offer #</SortableHeaderComponent>,
            width: 80
        },

        {
            colId: 'chainId',
            field: 'chainId',
            headerName: 'Swap #',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap #</SortableHeaderComponent>,
            width: 80,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as ICenterChainEndOffer;
                return <>{data.chainId} / {data.clusterNumber} / {data.positionNumber}</>
            }
        },

        {
            colId: 'donorCenter',
            field: 'donorCenter',
            headerName: 'Donor Center',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor<br />Center</SortableHeaderComponent>,
            width: 100
        },

        {
            colId: 'donorAlias',
            field: 'donorAlias',
            headerName: 'Donor Alias',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor<br />Alias</SortableHeaderComponent>,
            width: 100
        },

        {
            colId: 'donationType',
            field: 'donationType',
            headerName: 'Donation Type',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donation<br />Type</SortableHeaderComponent>,
            width: 150
        },

        {
            colId: 'recipientCenter',
            field: 'recipientCenter',
            headerName: 'Recipient Center',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient<br />Center</SortableHeaderComponent>,
            width: 100
        },

        {
            colId: 'recipientAlias',
            field: 'recipientAlias',
            headerName: 'Recipient Alias',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient<br />Alias</SortableHeaderComponent>,
            width: 100
        },

        {
            colId: 'recipientType',
            field: 'recipientType',
            headerName: 'Recipient Type',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient<br />Type</SortableHeaderComponent>,
            width: 150
        },

        {
            colId: 'epletMm',
            field: 'epletMm',
            headerName: 'Eplet MM',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Eplet MM</SortableHeaderComponent>,
            width: 80,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as ICenterChainEndOffer;
                return <SingleEpletMmRisk data={data.singleEpletMmRisk} isAdmin={false} centerId={data.recipientCenterId} />
            }
        },

        {
            colId: 'isAccepted',
            field: 'isAccepted',
            headerName: 'Decision',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Accepted</SortableHeaderComponent>,
            width: 105,
            cellRendererFramework: (props: ICellRendererParams) => props.value ? <span className='label label-success'>Yes</span> : <span className='label label-danger'>No</span>
        },

        {
            colId: 'offerDateTime',
            field: 'offerDateTime',
            headerName: 'Offer Date/Time',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Offer<br />Date/Time</SortableHeaderComponent>,
            width: 125,
            comparator: Utility.dateComparator
        },

        {
            colId: 'deadlineDateTime',
            field: 'deadlineDateTime',
            headerName: 'Deadline Date/Time',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Deadline<br />Date/Time</SortableHeaderComponent>,
            width: 125,
            comparator: Utility.dateComparator
        },

        {
            colId: 'decisionDateTime',
            field: 'decisionDateTime',
            headerName: 'Decision Date/Time',
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Decision<br />Date/Time</SortableHeaderComponent>,
            width: 125,
            comparator: Utility.dateComparator
        }
    ];

    const columnDefs = useMemo(() => columns, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <ReportWrapper className='ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    headerClass: 'list-header',
                    cellClass: 'list-cell',
                    sortable: true,
                    resizable: false
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={45}
                groupHeaderHeight={30}
                rowHeight={30}
                enableRangeSelection={true}
                noRowsOverlayComponentFramework={() => <div>No chain end offers found...</div>}
                onGridReady={props.onGridReady}
                onFirstDataRendered={props.onFirstDataRendered}
                onRowDataChanged={props.onRowDataChanged}
                onSortChanged={props.onSortChanged}
            />
        </ReportWrapper>
    );
}

export default Grid;